import Modal from '../Modal';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useModal } from '../../../providers/ModalProvider';
import { modals } from '../../../providers/modals';
import UserMultiSelector from '../../UserMultiSelector';
import { Text } from '../../typography';
import { useTranslation } from 'react-i18next';
import { keys } from '../../../utilities/translator/translation_keys';
import { Form } from 'react-bootstrap';
import styles from './RecordMeetingModal.module.scss';
import { useWorkspace } from '../../../providers/WorkspaceProvider';
import { loader } from 'graphql.macro';
import { useQuery } from '@apollo/client';
import { Icon } from '@mui/material';
import { showToast } from '../../../graphql/cache/modal';
import Button from '../../Button';
import { Spinner } from 'react-bootstrap';
import findFileIcon, { imageExtensions } from '../../../utilities/files';
import { fileType, toastLength, toastVariant } from '../../../constants/misc';
import { useCurrentUser } from '../../../providers/UserProvider';
import { customColors } from '../../../utilities';
import InspectionSignatures from '../../../pages/inspection_page/InspectionSignatures';
import SignatureCanvas from '../../SignatureCanvas';
import { useOnlineStatus } from '../../../hooks/offline-hooks/offline-misc';
import VoiceRecorder from '../../VoiceRecorder';
import QuickFileDropzone from '../../image_uploads/QuickFileDropzone';
import { useAIMutations } from '../../../hooks/AI/AI_hooks';
import { useVoiceRecorder } from '../../../providers/VoiceRecorderProvider';
import TextWithIcon from '../../typography/TextWithIcon';
import { useInspectionMutations } from '../../../hooks/inspection/inspection_auxilary';
import Dropdown from '../../dropdownV2/Dropdown';
import { useChangeWorkspace } from '../../../hooks/misc';
import SimpleWorkspaceCard from '../../SimpleWorkspaceCard';
import SimpleUserCard from '../../SimpleUserCard';
import moment from 'moment';
import { getLocalTime } from '../../../utilities/time';

const meetingQuery = loader('./RecordMeetingModal.fetch.graphql');
const openMeetingsQuery = loader('./RecordMeetingModal.open.graphql');

const POLL_INTERVAL = 5000;
const SIGNATURE_EXTENSION = 'SIGNATURE';
const AUDIO_FILE_TYPE = 'AUDIO';

export default function RecordMeetingModal() {
  const { closeModal, modalState, openConfirmationModal, updateModal } =
    useModal();
  const { user: currentUser } = useCurrentUser();
  const { workspaceId, workspace, availableWorkspaces } = useWorkspace();
  const { t } = useTranslation();

  const [loadingAttachment, setLoadingAttachment] = useState();
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [safetyTopic, setSafetyTopic] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [showSignatures, setShowSignatures] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);
  const [manual, setManual] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const seededIdRef = useRef(null);
  const attachedAudioUrlsRef = useRef(new Set());
  const pendingAudioRef = useRef(0);
  const [joiningId, setJoiningId] = useState(null);
  const startingRef = useRef(false);
  const workspaceOptions = useMemo(
    () => availableWorkspaces || [],
    [availableWorkspaces],
  );
  const { changeWorkspace } = useChangeWorkspace();

  const {
    updateInspection,
    addRegret,
    deleteRegret,
    addParticipant,
    deleteParticipant,
    addAttachment,
    deleteAttachment,
    deleteInspection,
    updateInspectionItem,
  } = useInspectionMutations();

  const online = useOnlineStatus();
  const { summarizeAudio } = useAIMutations();
  const {
    audioURL,
    stopRecording,
    setAudioURL,
    fullAudioFileRef,
    ready,
    setReady,
    isRecording,
  } = useVoiceRecorder();

  const { show, inspectionId, refetch, onView, startNew } =
    modalState.recordMeetingModal;

  // Lets async start/join work bail out if the modal was closed meanwhile
  const showRef = useRef(show);
  showRef.current = show;

  // Opened from a "start meeting" entry point: offer today's open meetings first
  const isPicking = show && !inspectionId && !!startNew;
  const startOfToday = useMemo(
    () => moment().startOf('day').toISOString(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPicking],
  );

  const { data: openMeetingsData, error: openMeetingsError } = useQuery(
    openMeetingsQuery,
    {
      skip: !isPicking || !online,
      fetchPolicy: 'network-only',
      variables: { from: startOfToday },
    },
  );
  const openMeetings = openMeetingsData?.inspections;

  const {
    data: { inspections: [inspection] = [] } = {},
    error: meetingError,
    refetch: refetchMeeting,
  } = useQuery(meetingQuery, {
    skip: !show || !inspectionId,
    variables: { id: `${inspectionId}` },
    pollInterval: show && online ? POLL_INTERVAL : 0,
  });

  const questions = useMemo(
    () => inspection?.template?.categories?.[0]?.questions || [],
    [inspection?.template],
  );

  const getItemValue = (questionTitle) => {
    const questionId = questions.find((q) => q.title === questionTitle)?.id;
    return (
      inspection?.items?.find((i) => i.questionId === questionId)?.value || ''
    );
  };

  // Seed the text inputs once per meeting so polling doesn't overwrite typing
  useEffect(() => {
    if (!inspection?.id || seededIdRef.current === inspection.id) return;
    seededIdRef.current = inspection.id;
    setTitle(inspection.subject || '');
    setNotes(inspection.notes || '');
    setManual(!!inspection.notes);
    setSafetyTopic(getItemValue('Safety Topic'));
    setLocation(getItemValue('Location'));
    setDescription(getItemValue('Meeting Description'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspection]);

  const attachments = useMemo(
    () => inspection?.attachments || [],
    [inspection?.attachments],
  );
  const signatures = useMemo(
    () => attachments.filter((a) => a.extension === SIGNATURE_EXTENSION),
    [attachments],
  );
  const files = useMemo(
    () =>
      attachments.filter(
        (a) =>
          a.extension !== SIGNATURE_EXTENSION && a.fileType !== AUDIO_FILE_TYPE,
      ),
    [attachments],
  );

  const isShared = !!inspection && inspection.workspaceId == null;
  const selectedWorkspace = useMemo(
    () =>
      workspaceOptions.find((w) => w.id === inspection?.workspaceId) ||
      workspace,
    [workspaceOptions, inspection?.workspaceId, workspace],
  );
  const userListType = isShared ? 'all' : 'workspace';

  const updateQuestion = ({ target, value }) => {
    const targetQuestionId = questions.find((q) => q.title === target)?.id;
    const inspectionItemId = inspection?.items?.find(
      (i) => i.questionId === targetQuestionId,
    )?.id;
    if (!inspectionItemId || !targetQuestionId) return;

    updateInspectionItem({ id: inspectionItemId, value });
  };

  const onClose = () => {
    stopRecording();
    setAudioURL(null);
    if (fullAudioFileRef?.current) {
      fullAudioFileRef.current = [];
    }
    attachedAudioUrlsRef.current = new Set();
    seededIdRef.current = null;
    setShowSignatures(false);
    setHasRecording(false);
    setManual(false);
    refetch();
    closeModal({ modalName: modals.recordMeeting });
  };

  const onSaveAndClose = () => {
    onClose();
    if (!inspection?.id) return;
    showToast({
      title: t(keys.assessments.MEETING_SAVED_TITLE),
      message: t(keys.assessments.MEETING_SAVED_MESSAGE),
      variant: toastVariant.info,
      time: toastLength.md,
    });
  };

  const onDiscard = () => {
    openConfirmationModal({
      title: t(keys.assessments.DISCARD_CONFIRMATION_TITLE),
      description: t(keys.assessments.DISCARD_CONFIRMATION_MESSAGE),
      variant: 'danger',
      buttonText: t(keys.assessments.DISCARD_MEETING),
      onSubmit: async () => {
        try {
          await deleteInspection({ id: inspection.id });
        } catch (err) {
          console.error(err);
        }
        onClose();
      },
    });
  };

  const onSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await summarizeAudio({ inspectionId: inspection.id });
      await updateInspection({ id: inspection.id, isDraft: false });
      onClose();
      showToast({
        title: t(keys.assessments.SUBMIT_TITLE),
        message: t(keys.assessments.SUBMIT_MESSAGE),
        variant: toastVariant.info,
        time: toastLength.md,
      });
    } catch (err) {
      console.error(err);
      showToast({
        title: t(keys.common.ERROR),
        message: t(keys.assessments.MEETING_ERROR),
        variant: toastVariant.danger,
        time: toastLength.md,
      });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  // Each uploaded chunk is attached once; a pending count keeps the spinner up
  // until every in-flight chunk has been attached
  useEffect(() => {
    const url = audioURL?.url;
    if (
      !url ||
      !ready ||
      !show ||
      !inspection?.id ||
      attachedAudioUrlsRef.current.has(url)
    ) {
      return;
    }
    attachedAudioUrlsRef.current.add(url);
    pendingAudioRef.current += 1;
    addAttachment({
      inspectionId: inspection.id,
      file: { ...audioURL, fileType: AUDIO_FILE_TYPE },
    })
      .catch((err) => console.error(err))
      .finally(() => {
        pendingAudioRef.current -= 1;
        if (pendingAudioRef.current === 0) setReady(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioURL, ready, inspection?.id, show]);

  const handleWorkspaceChange = (w) => {
    const id = w?.id ?? null;
    updateInspection({ id: inspection.id, workspaceId: id });
    if (id != null) changeWorkspace(id);
  };

  const showStartError = () =>
    showToast({
      title: t(keys.common.ERROR),
      message: t(keys.assessments.MEETING_ERROR),
      variant: toastVariant.danger,
      time: toastLength.md,
    });

  const onStartNew = async () => {
    if (startingRef.current) return;
    startingRef.current = true;
    try {
      const newInspectionId = await startNew();
      if (!showRef.current) return;
      updateModal({
        modalName: modals.recordMeeting,
        variables: { inspectionId: newInspectionId },
      });
    } catch (err) {
      console.error(err);
      showStartError();
      onClose();
    } finally {
      startingRef.current = false;
    }
  };

  const onJoin = async (meeting) => {
    if (joiningId) return;
    setJoiningId(meeting.id);
    try {
      if (meeting.creator?.id !== currentUser.id) {
        await addParticipant({
          userId: currentUser.id,
          inspectionId: meeting.id,
        });
      }
      if (!showRef.current) return;
      updateModal({
        modalName: modals.recordMeeting,
        variables: { inspectionId: meeting.id },
      });
    } catch (err) {
      console.error(err);
      showStartError();
    } finally {
      setJoiningId(null);
    }
  };

  // No open meetings to offer (none today, offline, or the lookup failed):
  // skip the picker and start one straight away
  useEffect(() => {
    if (
      isPicking &&
      (!online || !!openMeetingsError || openMeetings?.length === 0)
    ) {
      onStartNew();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPicking, openMeetings, openMeetingsError]);

  const pickerBody = !openMeetings?.length ? (
    <div className={styles.emptyState}>
      <br />
      <br />
      <br />
      <Spinner
        className={styles.spinner}
        animation="border"
        variant="primary"
      />
    </div>
  ) : (
    <div className={styles.picker}>
      <Text noMargin weight="semiBold" size="lg">
        {t(keys.assessments.OPEN_MEETINGS_TITLE)}
      </Text>
      <Text color="secondary">{t(keys.assessments.OPEN_MEETINGS_MESSAGE)}</Text>
      {openMeetings.map((meeting) => {
        const isOwn = meeting.creator?.id === currentUser?.id;
        return (
          <div key={meeting.id} className={styles.meetingCard}>
            <div>
              <Text noMargin weight="semiBold">
                {meeting.subject || t(keys.assessments.RECORDED_MEETING)}
              </Text>
              <SimpleUserCard
                user={meeting.creator}
                size="sm"
                disableClick
                smallText={getLocalTime(parseInt(meeting.dateCreated)).format(
                  'HH:mm',
                )}
              />
            </div>
            {joiningId === meeting.id ? (
              <Spinner
                className={styles.spinner}
                animation="border"
                variant="primary"
              />
            ) : (
              <Button
                disabled={!!joiningId}
                value={
                  isOwn
                    ? t(keys.assessments.RESUME_MEETING)
                    : t(keys.assessments.JOIN_MEETING, {
                        name: meeting.creator?.firstName,
                      })
                }
                onClick={() => onJoin(meeting)}
              />
            )}
          </div>
        );
      })}
      <Button
        outlined
        icon="add"
        disabled={!!joiningId}
        value={t(keys.assessments.START_NEW_MEETING)}
        onClick={onStartNew}
      />
    </div>
  );

  // Only the creator presents; everyone else who opens the meeting can only sign
  const isPresenter =
    !!inspection && inspection.creator?.id === currentUser?.id;
  const canSign = isPresenter || !!inspection?.isDraft;

  const onAddSignature = async (url, name) => {
    setShowSignatures(false);
    try {
      await addAttachment({
        inspectionId: inspection.id,
        file: { url, name, extension: SIGNATURE_EXTENSION },
      });
      if (!isPresenter) {
        await addParticipant({
          userId: currentUser.id,
          inspectionId: inspection.id,
        });
      }
    } catch (err) {
      console.error(err);
    }
    refetchMeeting();
  };

  const signatureSection = (
    <>
      <div className={styles.header}>
        <TextWithIcon
          icon="draw"
          noMargin
          weight="semiBold"
          iconProps={{ fontSize: '1.2rem' }}
        >
          {!!showSignatures ? 'Add Signature' : 'Signatures'}
        </TextWithIcon>
        {canSign && (
          <Icon
            onClick={() => setShowSignatures(!showSignatures)}
            sx={{ color: customColors.BLUE, cursor: 'pointer' }}
          >
            {showSignatures ? 'cancel' : 'add'}
          </Icon>
        )}
      </div>
      {online &&
        (!showSignatures ? (
          <div className={styles.signatures}>
            <InspectionSignatures
              size="sm"
              attachments={signatures}
              currentUserId={currentUser?.id}
              onDelete={(id) =>
                deleteAttachment({ id, onComplete: refetchMeeting })
              }
            />
          </div>
        ) : (
          <div className={styles.signatureContainer}>
            <SignatureCanvas
              onSubmit={(url, failed, pass, name) => onAddSignature(url, name)}
            />
          </div>
        ))}
    </>
  );

  const signOnlyBody = !!inspection && (
    <div className={styles.container}>
      <div className={styles.leftContainer}>
        <Text noMargin weight="semiBold">
          Meeting Title
        </Text>
        <Text>{inspection.subject || t(keys.common.NONE)}</Text>
        <Text noMargin weight="semiBold">
          {t(keys.common.CREATOR)}
        </Text>
        <Text>
          {`${inspection.creator?.firstName ?? ''} ${
            inspection.creator?.lastName ?? ''
          }`}
        </Text>
        <div className={styles.header}>
          <Text noMargin weight="semiBold">
            {t(keys.assessments.SAFETY_TOPIC)}
          </Text>
          {!!onView && (
            <Text
              hover
              onClick={() => onView()}
              noMargin
              size="sm"
              color="accentPrimary"
            >
              {t(keys.assessments.VIEW)}
            </Text>
          )}
        </div>
        <Text>{getItemValue('Safety Topic') || t(keys.common.NONE)}</Text>
        <Text noMargin weight="semiBold">
          {t(keys.assessments.LOCATION)}
        </Text>
        <Text>{getItemValue('Location') || t(keys.common.NONE)}</Text>
        <Text noMargin weight="semiBold">
          {t(keys.assessments.MEETING_DESCRIPTION)}
        </Text>
        <Text>
          {getItemValue('Meeting Description') || t(keys.common.NONE)}
        </Text>
      </div>
      <div className={styles.rightContainer}>
        <Text noMargin weight="semiBold">
          {t(keys.common.PARTICIPANTS)}
        </Text>
        <UserMultiSelector
          readOnly
          disableSelectAll
          type={userListType}
          selected={inspection.participants}
        />
        {signatureSection}
      </div>
    </div>
  );

  const footer = isPresenter && (
    <div className={styles.footer}>
      <div className={styles.check}>
        <div className={styles.checkContainer}>
          <Text noMargin weight="semiBold">
            {t(keys.common.SHARED)}
          </Text>
          <Form.Check
            type="switch"
            onChange={(e) =>
              updateInspection({
                id: inspection.id,
                workspaceId: e.target.checked ? null : workspaceId,
              })
            }
            checked={isShared}
          />
        </div>
      </div>
      {ready || submitting ? (
        <div>
          <Spinner
            className={styles.spinner}
            animation="border"
            variant="primary"
          />
        </div>
      ) : (
        <div className={styles.checkContainer}>
          <Button
            outlined
            variant="danger"
            icon="delete"
            disabled={isRecording}
            value={t(keys.assessments.DISCARD_MEETING)}
            onClick={onDiscard}
          />
          <Button
            disabled={isRecording || showSignatures}
            value={t(keys.action.SUBMIT)}
            onClick={() => {
              openConfirmationModal({
                title: t(keys.assessments.SUBMIT_CONFIRMATION_TITLE),
                description: t(keys.assessments.SUBMIT_CONFIRMATION_MESSAGE),
                variant: 'warning',
                onSubmit,
              });
            }}
          />
        </div>
      )}
    </div>
  );

  return (
    <Modal
      open={show || false}
      onClose={isPresenter ? onSaveAndClose : onClose}
      title={t(keys.assessments.RECORD_MEETING)}
      allowClose={!isRecording && !ready && !submitting}
      hideCancel
      hideSubmit
      beta
      footer={footer}
      className={styles.fullScreen}
      titleImage={
        <Text size="lg" noMargin>
          📆
        </Text>
      }
    >
      {isPicking ? (
        pickerBody
      ) : !inspection ? (
        <div className={styles.emptyState}>
          <br />
          <br />
          <br />
          {!!inspectionId && online && !meetingError ? (
            <Spinner
              className={styles.spinner}
              animation="border"
              variant="primary"
            />
          ) : (
            <>
              <Icon sx={{ fontSize: '3rem', color: customColors.ORANGE }}>
                warning
              </Icon>
              <Text noMargin textAlign="center" weight="semiBold">
                {t(keys.assessments.NO_MEETING_DATA)}
              </Text>
              <Text size="sm" textAlign="center">
                {t(keys.assessments.ISSUE_PERSIST)}
              </Text>
            </>
          )}
        </div>
      ) : !isPresenter ? (
        signOnlyBody
      ) : (
        <div className={styles.container}>
          <div className={styles.leftContainer}>
            <div className={styles.recorder}>
              <VoiceRecorder
                onStop={async () => {
                  setHasRecording(true);
                  await stopRecording();
                }}
                audioFile={inspection?.audioURL || audioURL}
                hasRecording={hasRecording}
              />
              <div className={styles.fullWidth}>
                {!manual ? (
                  <Text
                    textAlign="center"
                    color="accentPrimary"
                    hover
                    onClick={() => setManual(true)}
                    noMargin
                    className={styles.fullWidth}
                  >
                    {t(keys.assessments.ENTER_MANUALLY)}
                  </Text>
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    onBlur={(e) =>
                      updateInspection({
                        id: inspection.id,
                        notes: e.target.value,
                      })
                    }
                  />
                )}
              </div>
            </div>

            <div className={styles.spacer} />
            <Text noMargin weight="semiBold">
              Meeting Title
            </Text>
            <Form.Control
              onChange={(e) => setTitle(e.target.value)}
              value={title}
              onBlur={() => {
                updateInspection({ id: inspection.id, subject: title });
              }}
            />
            <div className={styles.header}>
              <Text noMargin weight="semiBold">
                {t(keys.assessments.SAFETY_TOPIC)}
              </Text>
              {!!onView && (
                <Text
                  hover
                  onClick={() => onView()}
                  noMargin
                  size="sm"
                  color="accentPrimary"
                >
                  {t(keys.assessments.VIEW)}
                </Text>
              )}
            </div>
            <Form.Control
              value={safetyTopic}
              as="textarea"
              rows={3}
              onChange={(e) => setSafetyTopic(e.target.value)}
              onBlur={() =>
                updateQuestion({
                  target: 'Safety Topic',
                  value: safetyTopic,
                })
              }
              className={styles.input}
            />
            <Text noMargin weight="semiBold">
              {t(keys.assessments.LOCATION)}
            </Text>
            <Form.Control
              as="textarea"
              rows={1}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className={styles.input}
              onBlur={(e) =>
                updateQuestion({
                  target: 'Location',
                  value: e.target.value,
                })
              }
            />
            <Text noMargin weight="semiBold">
              {t(keys.assessments.MEETING_DESCRIPTION)}
            </Text>
            <Form.Control
              as="textarea"
              value={description}
              rows={4}
              onChange={(e) => setDescription(e.target.value)}
              className={styles.input}
              onBlur={(e) =>
                updateQuestion({
                  target: 'Meeting Description',
                  value: e.target.value,
                })
              }
            />
          </div>
          <div className={styles.rightContainer}>
            <Text noMargin weight="semiBold">
              {t(keys.common.WORKSPACE)}
            </Text>
            {isRecording ? (
              <div className={styles.workspaceHolder}>
                <SimpleWorkspaceCard workspace={workspace} weight="regular" />
              </div>
            ) : (
              <Dropdown
                readOnly={isRecording}
                selected={isShared ? null : selectedWorkspace}
                options={workspaceOptions}
                onChange={handleWorkspaceChange}
                type="workspace"
              />
            )}
            <Text noMargin weight="semiBold">
              {t(keys.common.PARTICIPANTS)}
            </Text>
            <UserMultiSelector
              type={userListType}
              otherWorkspace={!isShared ? selectedWorkspace : null}
              parentId={inspection.id}
              parentType="inspection"
              selected={inspection.participants}
              setUsers={() => refetchMeeting()}
              onUserAdded={(user) =>
                addParticipant({
                  userId: user.id,
                  inspectionId: inspection.id,
                  onComplete: refetchMeeting,
                })
              }
              onUserRemoved={(user) =>
                deleteParticipant({
                  userId: user.id,
                  inspectionId: inspection.id,
                  onComplete: refetchMeeting,
                })
              }
            />
            <Text noMargin weight="semiBold">
              {t(keys.common.REGRETS)}
            </Text>
            <UserMultiSelector
              disableSelectAll
              type={userListType}
              otherWorkspace={!isShared ? selectedWorkspace : null}
              parentId={inspection.id}
              parentType="regret"
              selected={inspection.regrets}
              onUserAdded={(user) =>
                addRegret({
                  userId: user.id,
                  inspectionId: inspection.id,
                  onComplete: refetchMeeting,
                })
              }
              onUserRemoved={(user) =>
                deleteRegret({
                  userId: user.id,
                  inspectionId: inspection.id,
                  onComplete: refetchMeeting,
                })
              }
            />
            {loadingAttachment ? (
              <Spinner
                className={styles.spinner}
                animation="border"
                variant="primary"
              />
            ) : (
              <>
                <QuickFileDropzone
                  header={t(keys.common.ATTACHMENTS)}
                  icon={!!files.length}
                  iconText={null}
                  setLoading={setLoadingAttachment}
                  iconSize={'1.5rem'}
                  onSubmit={({ url, name }) => {
                    setLoadingAttachment(true);
                    const extension = url.split('.').pop();
                    addAttachment({
                      inspectionId: inspection.id,
                      file: {
                        url,
                        name,
                        fileType: fileType[extension],
                        extension,
                      },
                      onComplete: refetchMeeting,
                    })
                      .catch((err) => console.error(err))
                      .finally(() => setLoadingAttachment(false));
                  }}
                />
                {files.map((a) => {
                  const isImage = imageExtensions.includes(a.extension);
                  const src = isImage ? a.url : findFileIcon(a.extension);
                  return (
                    <div className={styles.flexRow} key={a.id}>
                      <img className={styles.fileImage} src={src} alt="file" />
                      <Text noMargin>{a.name}</Text>
                      <Icon
                        baseClassName="material-icons-outlined"
                        style={{ color: 'red', fontSize: '1.2rem' }}
                        onClick={() =>
                          deleteAttachment({
                            id: a.id,
                            onComplete: refetchMeeting,
                          })
                        }
                      >
                        delete
                      </Icon>
                    </div>
                  );
                })}
              </>
            )}
            {signatureSection}
          </div>
        </div>
      )}
    </Modal>
  );
}
